import { useCallback } from 'react';
import { selectSections, updateGridPositionConfig } from '../grid/reduxStore/editorSlice';
import { useAppDispatch, useAppSelector } from '../grid/reduxStore/Store';
import { updateBlockPositionViaSocket } from '../grid/reduxStore/saveHandlers';
import { useSocketClient } from '../../../providers/SocketContext';
import { useCurrentUser } from '../../../providers/UserProvider';
import type { MovedBlock } from '../UndoRedo/UpdateDocumentSettingsCommand';

const RIGHT_EDGE_THRESHOLD_PX = 4;

export function useRepositionBlocksOnCanvasResize() {
  const dispatch = useAppDispatch();
  const { socketClient } = useSocketClient();
  const { data: user } = useCurrentUser();
  const sections = useAppSelector(selectSections);

  return useCallback(
    async (previousCanvasWidth: number, newCanvasWidth: number): Promise<MovedBlock[]> => {
      if (newCanvasWidth >= previousCanvasWidth) return [];

      const movedBlocks: MovedBlock[] = [];
      const socketUpdates: Promise<unknown>[] = [];

      for (const section of sections) {
        for (const [blockId, blockContent] of Object.entries(section.blocksContent)) {
          const { x, y, width } = blockContent.blockConfig;

          if (newCanvasWidth - x <= RIGHT_EDGE_THRESHOLD_PX) {
            const newX = Math.max(0, newCanvasWidth - width);

            movedBlocks.push({ blockId, sectionId: section.id, originalX: x, clampedX: newX, y });

            dispatch(updateGridPositionConfig({ sectionId: section.id, blockId, x: newX, y }));

            socketUpdates.push(
              updateBlockPositionViaSocket(socketClient, {
                blockId,
                userId: user.id,
                position: { top_px: y, left_px: newX },
              }).catch(() => undefined)
            );
          }
        }
      }

      await Promise.all(socketUpdates);
      return movedBlocks;
    },
    [dispatch, socketClient, user.id, sections]
  );
}
